import { AddressType, defaultAddressData, AddressFieldStateType } from './types';
import { Country } from '@buy-viasat/types/build/bv';

export const injectAddressData = ({
  addressLine1,
  addressLine2,
  municipality,
  region,
  postalCode,
  countryCode,
}: {
  addressLine1?: string;
  addressLine2?: string;
  municipality?: string;
  region?: string;
  postalCode?: string;
  countryCode?: string;
}): AddressFieldStateType => ({
  addressLines: {
    value: [
      addressLine1 ?? defaultAddressData(Country.US).addressLines[0],
      addressLine2 ?? defaultAddressData(Country.US).addressLines[1],
    ],
    valid: null,
    hintText: 'Address is required',
  },
  municipality: {
    value: municipality ?? defaultAddressData(Country.US).municipality,
    valid: null,
    hintText: 'City is required',
  },
  region: { value: region ?? defaultAddressData(Country.US).region, valid: null, hintText: 'State is required' },
  postalCode: {
    value: postalCode ?? defaultAddressData(Country.US).postalCode,
    valid: null,
    hintText: 'Zipcode is required',
  },
  countryCode: {
    value: countryCode ?? defaultAddressData(Country.US).countryCode,
    valid: null,
    hintText: 'CountryCode is required',
  },
  houseNumber: {
    value: '',
    valid: null,
    hintText: 'House Number is required',
  },
});

// Overrides an address's lines with the exact addressLines scrubAddress returned (apt/unit intact),
// mirroring plan/sanitizeAddressLines. Falls back to the address's own lines before the first scrub.
export const applyUnsanitizedAddressLines = (
  address: AddressType,
  unsanitizedAddressLines: string[] | null,
): AddressType =>
  unsanitizedAddressLines?.length ? { ...address, addressLines: unsanitizedAddressLines } : address;

export const hasAddressBeenChanged = (addressA: AddressType, addressB: AddressType): boolean => {
  return (
    addressA.addressLines[0] !== addressB.addressLines[0] ||
    addressA.addressLines[1] !== addressB.addressLines[1] ||
    addressA.municipality !== addressB.municipality ||
    addressA.region !== addressB.region ||
    addressA.postalCode !== addressB.postalCode ||
    addressA.countryCode !== addressB.countryCode ||
    addressA.houseNumber !== addressB.houseNumber
  );
};

/**
 * GFBAPPS-1993: PAL returns the secondary unit in a different shape per provider, because the two
 * responses are enriched by different geocoders behind the same /geo/addresses endpoint:
 *  - Fiber:  ['301 ENTERPRISE ST # 12']   unit inlined with ' # '
 *  - FSI: ['380 Faith Ln', '#12']      unit as a separate, '#'-prefixed element * 
 * Normalize all of them to a single [street, unit] shape . The raw response is kept separately as unsanitizedAddressLines.
 */
export const normalizeScrubbedAddressLines = (addressLines: string[]): string[] => {
  const stripUnitPrefix = (unit: string): string => unit.replace(/^\s*#\s*/, '').trim();

  if (addressLines.length > 1) {
    return addressLines.map((line, i) => (i === 0 ? line.trim() : stripUnitPrefix(line)));
  }

  const [line = ''] = addressLines;
  if (line.includes(',')) {
    return line.split(',').map((part, i) => (i === 0 ? part.trim() : stripUnitPrefix(part)));
  }
  if (line.includes('#')) {
    const hashIndex = line.indexOf('#');
    return [line.slice(0, hashIndex).trim(), stripUnitPrefix(line.slice(hashIndex))];
  }
  return [line.trim()];
};

// Normalizes address line 1 by ensuring the subunit appears once,
// at the end of the string, preceded by a comma.
export function normalizeAddressLine1(fullAddress: string, subunit: string): string {
  // Remove '#' from the full address
  const cleanedFullAddress = fullAddress.replace('#', '');

  // Remove the subunit part from the end (case-insensitive, and only at the end)
  // This covers cases where the subunit looks like this:
  // apt 1-2, apt (rear), unit #3, suite + 2
  const escapedSubunit = subunit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`${escapedSubunit}$`, 'i');
  const cleanedFullAddressWithoutSubunit = cleanedFullAddress.replace(regex, '');

  // Remove trailing comma if any
  const cleanedAddressWithoutComma = cleanedFullAddressWithoutSubunit.replace(/,\s*$/, '').trim();

  return `${cleanedAddressWithoutComma}, ${subunit}`;
}

// Resolves how a scrubbed address should be handled based on its MDU-fiber status
// and whether the user supplied an apt/unit (subunit):
// - needsUnit: MDU fiber address without a subunit — stop and prompt the user for one.
// - shouldNormalizeLine1: a subunit is present, so line 1 should be normalized with it.
export function resolveFiberMduAddress(
  hasFiberMduAddress: boolean,
  addressLines: string[],
  subunit: string,
): { needsUnit: boolean; shouldNormalizeLine1: boolean } {
  return {
    needsUnit: hasFiberMduAddress && subunit.length === 0,
    shouldNormalizeLine1: subunit.length > 0 && Boolean(addressLines[0]),
  };
}

// Returns a copy of the scrubbed address with addressLines[0] normalized.
// Input may contain subunits in different formats (with/without comma or '#'),
// but the output always uses: "<base address>, <subunit>".
export function withNormalizedLine1(scrubAddress: AddressType, subunit: string): AddressType {
  const [line1 = '', line2 = ''] = scrubAddress.addressLines ?? [];
  const normalizedLine1 = normalizeAddressLine1(line1, subunit);

  return {
    ...scrubAddress,
    addressLines: [normalizedLine1, line2],
  };
}
